import { ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { OverturAdminSubInfo } from '../../../interfaces/overtur-admin-subs';
import { BackendAccessService } from '../../../Services/APICalls/backend-access-service';

import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { LoadingService } from '../../../Services/Loading/loading-service.service';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { DeleteDialogComponent } from '../delete-dialog/delete-dialog.component';
import { Router } from '@angular/router';

@Component({
  selector: 'app-landing-sub-list-page',
  templateUrl: './landing-sub-list-page.component.html',
  styleUrls: ['./landing-sub-list-page.component.scss'],

  standalone: false,
})
/* istanbul ignore next */
export class LandingSubListPageComponent {
  overturAdminsubscriptions: OverturAdminSubInfo[] = [];
  displayedColumns: string[] = [
    'companyName',
    'endUserEmail',
    'subscriptionId',
    'sku',
    'startDate',
    'expirationDate',
    'subscriptionStatus',
    'actions',
  ];

  statusFilter: string = '';
  startDateFilter: Date | null = null;
  expirationDateFilter: Date | null = null;
  companyNameFilter: string = '';
  emailFilter: string = '';
  subscriptionIdFilter: string = '';
  skuFilter: string = '';

  dataSource = new MatTableDataSource<OverturAdminSubInfo>();

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private readonly backend: BackendAccessService,
    private readonly loading: LoadingService,
    private readonly dialog: MatDialog,
    private readonly cdRef: ChangeDetectorRef,
    private readonly router: Router
  ) {}
  /* istanbul ignore next */
  ngOnInit(): void {
    this.loading.show();

    this.dataSource.filterPredicate = (
      data: OverturAdminSubInfo,
      filter: string
    ) => {
      const filters = JSON.parse(filter);

      const statusMatch =
        !filters.status || data.subscriptionStatus === filters.status;

      const dataStartDate = data.startDate ? new Date(data.startDate) : null;
      const filterStartDate = filters.startDate
        ? new Date(filters.startDate)
        : null;
      const startDateMatch =
        !filterStartDate ||
        (dataStartDate &&
          dataStartDate.toISOString().split('T')[0] ===
            filterStartDate.toISOString().split('T')[0]);

      const dataExpirationDate = data.expirationDate
        ? new Date(data.expirationDate)
        : null;
      const filterExpirationDate = filters.expirationDate
        ? new Date(filters.expirationDate)
        : null;
      const expirationDateMatch =
        !filterExpirationDate ||
        (dataExpirationDate &&
          dataExpirationDate.toISOString().split('T')[0] ===
            filterExpirationDate.toISOString().split('T')[0]);

      const companyNameFilter = filters.companyName?.toLowerCase() || '';
      const companyNameMatch =
        !companyNameFilter ||
        data.companyName?.toLowerCase().includes(companyNameFilter);

      const emailMatch =
        !filters.email ||
        data.endUserEmail?.toLowerCase().includes(filters.email);

      const subscriptionIdMatch =
        !filters.subscriptionId ||
        data.subscriptionId?.toLowerCase().includes(filters.subscriptionId);

      const skuFilter = filters.sku?.toLowerCase() || '';
      const skuMatch =
        !skuFilter || data.sku?.toLowerCase().includes(skuFilter);

      return Boolean(
        statusMatch &&
          startDateMatch &&
          expirationDateMatch &&
          companyNameMatch &&
          emailMatch &&
          subscriptionIdMatch &&
          skuMatch
      );
    };

    this.backend.getOverturSubListLanding().subscribe({
      next: (data) => {
        this.overturAdminsubscriptions = data;
        this.dataSource.data = data;
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
        this.applyFilters(); // apply initial empty filter
      },
      error: (error) => {
        console.error('Error fetching subscriptions:', error);
        this.loading.hide();
      },
      complete: () => {
        this.loading.hide();
      },
    });
  }

  /* istanbul ignore next */
  applyFilters(): void {
    const filterValues = {
      status: this.statusFilter,
      startDate: this.startDateFilter
        ? this.startDateFilter.toISOString().split('T')[0]
        : '',
      expirationDate: this.expirationDateFilter
        ? this.expirationDateFilter.toISOString().split('T')[0]
        : '',
      companyName: this.companyNameFilter.trim().toLowerCase(),
      email: this.emailFilter.trim().toLowerCase(),
      subscriptionId: this.subscriptionIdFilter.trim().toLowerCase(),
      sku: this.skuFilter.trim().toLowerCase(),
    };

    this.dataSource.filter = JSON.stringify(filterValues);

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  /* istanbul ignore next */
  onStatusChange(newStatus: string) {
    this.statusFilter = newStatus;
    this.applyFilters();
  }

  /* istanbul ignore next */
  onStartDateChange(newDate: Date | null) {
    this.startDateFilter = newDate;
    this.applyFilters();
  }

  /* istanbul ignore next */
  onExpirationDateChange(newDate: Date | null) {
    this.expirationDateFilter = newDate;
    this.applyFilters();
  }

  /* istanbul ignore next */
  editSubItem(element: any) {
    console.log('Edit item clicked for:', element);
    if (element?.orderId) {
      this.router.navigate(['/overtur-subscription-details'], {
        queryParams: { orid: element.orderId, subid: element.subscriptionId },
      });
    } else {
      console.error('Missing orderId in element:', element);
    }
  }
  /* istanbul ignore next */
  deleteSubItem(element: any) {
    const dialogRef = this.dialog.open(DeleteDialogComponent, {
      width: '400px',
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'confirm') {
        this.backend.deleteOverturAdminTrial(element).subscribe({
          next: (response) => {
            this.overturAdminsubscriptions =
              this.overturAdminsubscriptions.filter(
                (item) =>
                  String(item.subscriptionId) !== String(element.subscriptionId)
              );

            this.dataSource.data = [...this.overturAdminsubscriptions];
            this.dataSource.paginator = this.paginator;
            this.dataSource.sort = this.sort;

            this.cdRef.detectChanges();

            if (this.dataSource.paginator) {
              this.dataSource.paginator.firstPage();
            }
          },
          error: (err) => {
            console.error('Error deleting trial:', err);
          },
        });
      } else {
        console.log('Deletion cancelled');
      }
    });
  }
  /* istanbul ignore next */
  onStartDateInput(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.value) {
      // User cleared the input, reset the filter date and apply filters immediately
      this.startDateFilter = null;
      this.applyFilters();
    }
  }
  /* istanbul ignore next */
  onExpirationDateInput(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.value) {
      this.expirationDateFilter = null;
      this.applyFilters();
    }
  }
}
